/* eslint-disable react/no-string-refs */
import React from 'react';
import { PropTypes as PT } from 'prop-types';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import SVG from 'react-inlinesvg';
import personLockIcon from '@lifesize/ux-assets/vectors/svgIcons/person-lock.svg';
import caretUpIcon from '@lifesize/ux-assets/vectors/svgIcons/caret-up.svg';
import caretDownIcon from '@lifesize/ux-assets/vectors/svgIcons/caret-down.svg';
import _get from 'lodash/get';
import _compact from 'lodash/compact';
import StartingCall from 'components/StartingCallModal/StartingCallModal';
import Button from 'components/Button/Button';
import AddParticipantSearch from 'containers/AddParticipantDrawer/AddParticipantSearchContainer';
import lsConfig from 'utils/global-config';
import logger from 'utils/logger';
import classes from './AddParticipantDrawer.module.scss';


const INVITE_CONTAINER_ID = 'inviteLink';
const messages = defineMessages({
  addParticipant: { id: 'components.addParticipant.title', defaultMessage: 'Add a Participant' }
});
let participantSuccessMessageTimeout = null;

class AddParticipantDrawer extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      showInstructions: false,
      containerHeight: 0
    };
    this.redialCall = this.redialCall.bind(this);
    this.toggleInstructions = this.toggleInstructions.bind(this);
  }

  componentDidMount() {
    this.handleResize = this.updateDimensions.bind(this);
    this.handleResize();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    switch (this.state.userTriggeredInstructions) {
      case 'open':
        this.setState({ showInstructions: true });
        break;
      case 'closed':
        this.setState({ showInstructions: nextProps.manualDial });
        break;
      default:
        if (nextProps.manualDial) {
          this.setState({ showInstructions: true });
        }
        if (!nextProps.manualDial) {
          this.setState({ showInstructions: false });
        }
    }

    if (!this.props.callingError &&
      this.props.invitee &&
      !nextProps.invitee &&
      (nextProps.isGalaxyCall || nextProps.participantAdded)
    ) {
      this.setState({ addParticipantInvitee: this.props.invitee });
      participantSuccessMessageTimeout = setTimeout(() => {
        this.setState({ addParticipantInvitee: false });
      }, 3000);
    }

    if (!this.props.isVisible && nextProps.isVisible) {
      window.addEventListener('keydown', this.handleKeyDown);
      window.addEventListener('resize', this.handleResize);
    } else if (this.props.isVisible && !nextProps.isVisible) {
      window.removeEventListener('keydown', this.handleKeyDown);
      window.removeEventListener('resize', this.handleResize);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('resize', this.handleResize);
    if (this.props.invitee) {
      this.props.endOutgoingCall(this.props.invitee);
    }
    clearTimeout(participantSuccessMessageTimeout);
  }

  getContactType = (contact = {}) => {
    if (!contact) return 'manual';
    if (contact.isRoomSystem) return 'roomSystem';
    if (contact.isUser) return 'user';
    if (contact.isManual) return 'manual';
    return 'user';
  };

  handleEmailClick = async () => {
    try {
      const mailto = await this.props.getInviteMailLinkForExtension();
      // Create and click email link
      const element = document.createElement('a');
      element.setAttribute('href', mailto);
      if(!this.props.isDesktop) {
        element.setAttribute('target', '_blank');
      }
      element.style.display = 'none';
      document.body.appendChild(element);

      lsConfig.skipTimeOnBeforeUnload(1000);
      element.click();

      document.body.removeChild(element);
    } catch (err) {
      logger.error(`Unable to create mailto link: ${err}`);
    }
  };

  handleKeyDown = (evt) => {
    if (this.props.isVisible && evt.which === 13 && this.props.contact.name) {
      evt.preventDefault();
      this.props.call(this.props.contact);
    }
  };

  updateDimensions() {
    // eslint-disable-next-line react/no-string-refs
    if (!this.refs.container) return;
    // eslint-disable-next-line react/no-string-refs
    this.setState({ containerHeight: this.refs.container.clientHeight });
  }

  cancelCall = () => {
    this.props.clearOutgoingCall(this.props.invitee);
  };

  redialCall() {
    this.props.call(this.props.invitee);
  }

  toggleInstructions() {
    const { showInstructions } = this.state;
    this.setState({
      showInstructions: !showInstructions,
      userTriggeredInstructions: !showInstructions ? 'open' : 'closed'
    });
  }

  showInstructionLine() {
    const { showInstructions } = this.state;
    const instructionCaret = showInstructions ? caretUpIcon : caretDownIcon;
    const instructionCaretTestId = showInstructions ? 'caretUpIcon' : 'caretDownIcon';
    if (this.props.manualDial) {
      return (
        <FormattedMessage
          id="components.startCallModal.manualCall.instructions.toggle"
          description="Offer instructions for calling people not in your address book"
          defaultMessage="How to call someone not in your directory"
        />
      );
    }

    return (
      <button
        type="button"
        onClick={this.toggleInstructions}
        data-testid="add-participant-instructions-btn"
      >
        <FormattedMessage
          id="components.startCallModal.manualCall.instructions.toggle"
          description="Offer instructions for calling people not in your address book"
          defaultMessage="How to call someone not in your directory"
        />
        <SVG src={instructionCaret} className={classes.caret} data-testid={instructionCaretTestId} />
      </button>);
  }

  visibleInstructions() {
    const { showInstructions } = this.state;
    const { manualDial, searchString, contact } = this.props;
    if ((!manualDial && searchString) || contact.name) {
      return null;
    }

    return (<div className={classes.instructionContainer}>
      <div className={classes.toggle}>
        {this.showInstructionLine()}
      </div>
      <div className={`${classes.instructions} ${showInstructions ? classes.show : ''}`}>
        <div className={classes.title}>
          <FormattedMessage
            id="components.addParticipantDrawer.manualCall.instructions.title"
            description="Prompt user to follow one of the sample formats"
            defaultMessage="Use one of these formats:"
          />
        </div>
        <div className={classes.details}>
          <div>
            <dl>
              <dt>
                <FormattedMessage
                  id="components.startCallModal.manualCall.instructions.format.ip"
                  description="IP address"
                  defaultMessage="IP address"
                />
              </dt>
              <dd>203.0.113.0</dd>
              <dt>
                <FormattedMessage
                  id="components.startCallModal.manualCall.instructions.format.ipAndExt"
                  description="IP address and extension"
                  defaultMessage="IP address and extension"
                />
              </dt>
              <dd>203.0.113.0##1234567</dd>
            </dl>
          </div>
          <div>
            <dl>
              <dt>
                <FormattedMessage
                  id="components.startCallModal.manualCall.instructions.format.video"
                  description="Video address"
                  defaultMessage="Video address"
                />
              </dt>
              <dd>
                <FormattedMessage
                  id="components.startCallModal.manualCall.instructions.example.email"
                  description="Example Email"
                  defaultMessage="name@company.com"
                />
              </dd>
              <dt>
                <FormattedMessage
                  id="components.startCallModal.manualCall.instructions.format.lifesize"
                  description="Lifesize extension"
                  defaultMessage="Lifesize extension"
                />
              </dt>
              <dd>1234567</dd>
            </dl>
          </div>
        </div>
      </div>
    </div>);
  }

  callButton() {
    if (this.props.contact.name) {
      return (
        <section className={classes.buttonSection}>
          <Button
            disabled={!this.props.canAddParticipant}
            onClick={() => {
              this.props.call(this.props.contact);
            }}
            style={{ minWidth: 190, padding: 4, width: 190 }}
            data-testid="add-participant-call-btn"
          >
            <FormattedMessage
              id="components.addParticipant.call"
              defaultMessage="Call"
            />
          </Button>
        </section>
      );
    }

    return '';
  }

  render() {
    const { calling, callType, contact, intl, invitee, isLockedByModerator, isVisible } = this.props;
    const remoteContact = Object.keys(contact).length ? contact : _get(invitee, 'contact');

    if (this.state.addParticipantInvitee) {
      const addParticipantInvitee = this.state.addParticipantInvitee;
      if (!isVisible) return null;
      return (
        <StartingCall
          callName={_get(addParticipantInvitee, 'contact.name') || _get(addParticipantInvitee, 'dialString')}
          callDialString={_get(addParticipantInvitee, 'dialString')}
          callStatus={'success'} // temporary: for QA of WEB-1961
          callType={this.getContactType(addParticipantInvitee.contact)}
          cancelCall={this.cancelCall}
          clearCall={this.cancelCall}
          intl={intl}
          redialCall={this.redialCall}
          callTitle={intl.formatMessage(messages.addParticipant)}
          callingError={false}
          drawerCall
        />
      );
    }

    if (contact && calling && invitee && invitee.dialString) {
      if (!isVisible) return null;
      return (
        <StartingCall
          callName={_get(invitee, 'contact.name') || _get(invitee, 'dialString')}
          callDialString={_get(invitee, 'dialString')}
          callStatus={callType} // temporary: for QA of WEB-1961
          callType={this.getContactType(remoteContact)}
          cancelCall={this.cancelCall}
          intl={intl}
          redialCall={this.redialCall}
          callTitle={intl.formatMessage(messages.addParticipant)}
          callingError={this.props.callingError}
          drawerCall
          clearCall={() => {
            this.props.clearOutgoingCall(invitee);
          }}
          callFailureTimeout={10000}
        />
      );
    }

    const containerClasses = _compact([
      classes.container,
      isVisible ? '' : classes.hiddenDrawer
    ]).join(' ');
    return (
      <div
        className={containerClasses}
        ref="container"
      >
        <header>
          <h6>
            {intl.formatMessage(messages.addParticipant)}
          </h6>
        </header>
        {isLockedByModerator ? (
          <div className={classes.lockedMessage}>
            <SVG src={personLockIcon} data-testid="personLockIcon" className={classes.icon} />
            <FormattedMessage
              id="components.inCall.tooltip.cannotAddPart"
              defaultMessage="This meeting has been locked by the moderator"
            />
          </div>
        ) : (
          <>
            <div className={classes.content}>
              <AddParticipantSearch
                contact={contact}
                inviteContainerId={INVITE_CONTAINER_ID}
              />
            </div>
            {this.visibleInstructions()}
            {this.callButton()}
            <div
              className={classes.inviteLink}
              id={INVITE_CONTAINER_ID}
            >
              <a
                tabIndex="inviteLink"
                onClick={this.handleEmailClick}
                data-testid="add-participant-invite-link"
              >
                <FormattedMessage
                  id="components.addParticipant.invite"
                  defaultMessage="Invite someone by email"
                />
              </a>
            </div>
          </>
        )}
      </div>
    );
  }
}

AddParticipantDrawer.propTypes = {
  call: PT.func.isRequired,
  callType: PT.string,
  calling: PT.bool,
  callingError: PT.bool,
  canAddParticipant: PT.bool,
  clearOutgoingCall: PT.func,
  contact: PT.object,
  endOutgoingCall: PT.func,
  getInviteForExtension: PT.func,
  getInviteMailLinkForExtension: PT.func,
  /* eslint-enable react/no-unused-prop-types */
  intl: PT.object,
  invitee: PT.object,
  isGalaxyCall: PT.bool,
  isLockedByModerator: PT.bool,
  isVisible: PT.bool,
  /* eslint-disable react/no-unused-prop-types */
  manualDial: PT.bool,
  participantAdded: PT.any,
  searchString: PT.string,
  isDesktop: PT.bool
};

export default injectIntl(AddParticipantDrawer);
